import { useEffect, useState, type ReactNode } from 'react';
import { Outlet, Route, Routes, useNavigate, useOutletContext, useParams } from 'react-router';
import {
  daysTo,
  eventDates,
  eventName,
  isFileType,
  itemFor,
  itemTitle,
  readingItems,
  showGaps,
  sortItems,
  TRAVEL_TYPE_LABELS,
  travelIcs,
  tripGaps,
  type ConfirmationReading,
  type TravelDraft,
  type TravelItem,
  travelReadFailureText,
} from '@wr/shared';
import { ApiError, del, errorMessage } from '../../api.js';
import { Icon, type IconName } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { EmptyState, Notice, PageHead } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import { useSignedIn } from '../../session.js';
import {
  documentUrl,
  fmtDateTime,
  fmtDay,
  itemFileUrl,
  loadTrip,
  loadTrips,
  mapLink,
  openFile,
  saveText,
  today,
  type Trip,
  type TripPage,
} from './data.js';
import { AddWindow, DraftWindow, ItemWindow, WayWindow } from './ItemWindow.js';
import { LogisticsWindow, TeamWindow } from './LogisticsWindow.js';
import { PrepareOffline } from '../content/PrepareOffline.js';

export interface TripContext {
  page: TripPage;
  reload(): void;
}

/**
 * Travel (brief section 7.3, docs/prototype.html), mounted at /travel/*:
 * My trips, then each trip's page (available offline once opened) with
 * its windows: Add, an item, the show logistics and the team view.
 */
export function TravelScreen() {
  return (
    <Routes>
      <Route index element={<TripList />} />
      <Route path=":eventId" element={<TripView />}>
        <Route index element={null} />
        <Route path="add" element={<AddWindow />} />
        <Route path="add/:type" element={<ItemWindow />} />
        <Route path="add/:type/how" element={<WayWindow />} />
        <Route path="drafts/:draftId" element={<DraftWindow />} />
        <Route path="items/:itemId" element={<ItemWindow />} />
        <Route path="logistics" element={<LogisticsWindow />} />
        <Route path="team" element={<TeamWindow />} />
      </Route>
    </Routes>
  );
}

const when = (d: number | null, status: string) =>
  d !== null && d > 0 ? `In ${d} day${d > 1 ? 's' : ''}` : status === 'active' ? 'Now' : '';

function TripList() {
  const { store, unavailable } = useOffline();
  const navigate = useNavigate();
  const { can } = useSignedIn();
  const teamView = can('travel.team.view');
  const logistics = can('travel.logistics.manage');
  const [trips, setTrips] = useState<Trip[] | null>(null);
  const [offline, setOffline] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // The device's copy is what shows without network: wait for it to open.
    if (!store && !unavailable) return;
    let current = true;
    loadTrips(store).then(
      (r) => {
        if (!current) return;
        setTrips(r.trips);
        setOffline(r.offline);
      },
      (err: unknown) => current && setError(errorMessage(err)),
    );
    return () => {
      current = false;
    };
  }, [store, unavailable]);

  const mine = trips?.filter((t) => t.mine) ?? [];
  const others = trips?.filter((t) => !t.mine) ?? [];
  const card = (t: Trip) => {
    const d = daysTo(t.event.starts_on, today());
    const out = t.outbound;
    const name = eventName(t.event);
    const summary = (
      <>
        <span className="trip-when">{when(d, t.event.status)}</span>
        <b>{name}</b>
        <span className="note">
          {[t.event.location, eventDates(t.event)].filter(Boolean).join(', ')}
        </span>
        {t.mine ? (
          <>
            <span className="trip-sum">
              {out && (
                <>
                  <Icon name="plane" />
                  {itemTitle(out)}, {fmtDateTime(out.fields.depart)}
                </>
              )}
            </span>
            {t.gaps.length ? (
              <span className="trip-gap">{t.gaps.join(', ')}</span>
            ) : (
              <span className="trip-ok">Flights and hotel booked</span>
            )}
          </>
        ) : (
          <span className="trip-sum">
            {t.team.length ? t.team.map((m) => m.name).join(', ') : 'No team yet'}
          </span>
        )}
      </>
    );
    if (!teamView && !logistics) {
      return (
        <button
          key={t.event.id}
          type="button"
          className="trip-card"
          onClick={() => navigate(t.event.id)}
        >
          {summary}
        </button>
      );
    }
    // Admins: each show also opens its team view and its logistics (round 2).
    return (
      <div key={t.event.id} className="trip-card trip-admin">
        <button
          type="button"
          className="trip-open"
          aria-label={`${name}: open the trip`}
          onClick={() => navigate(t.event.id)}
        >
          {summary}
        </button>
        <span className="actions">
          {teamView && (
            <button
              type="button"
              className="btn sm"
              aria-label={`Team view: ${name}`}
              onClick={() => navigate(`${t.event.id}/team`)}
            >
              Team view
            </button>
          )}
          {logistics && (
            <button
              type="button"
              className="btn sm"
              aria-label={`Edit show logistics: ${name}`}
              onClick={() => navigate(`${t.event.id}/logistics`)}
            >
              Edit show logistics
            </button>
          )}
        </span>
      </div>
    );
  };

  return (
    <>
      <PageHead
        title="Travel"
        lede="Your shows, flights, hotel, opening hours and booth details. Available offline once opened."
      />
      {error && <Notice kind="error">{error}</Notice>}
      {offline && <Notice>You are offline: the trips this device keeps.</Notice>}
      {!trips && !error && <div className="spinner" aria-label="Loading" />}
      {mine.map((t) => (
        <PrepareOffline key={t.event.id} event={t.event} name={eventName(t.event)} />
      ))}
      {trips &&
        (mine.length ? (
          <div className="trip-list">{mine.map(card)}</div>
        ) : (
          <EmptyState title="No trip planned for you.">
            Ask marketing to add you to the team of a show.
          </EmptyState>
        ))}
      {others.length > 0 && (
        <>
          <h2 className="sec travel-others">
            {teamView || logistics ? 'Other active shows' : 'Other team trips'}
          </h2>
          <div className="trip-list">{others.map(card)}</div>
        </>
      )}
    </>
  );
}

function Section({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon: IconName;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="tsec" aria-label={title}>
      <div className="tsec-h">
        <Icon name={icon} />
        <h2>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  if (children === '' || children === null || children === undefined) return null;
  return (
    <div className="trow">
      <span>{label}</span>
      <b>{children}</b>
    </div>
  );
}

function LinkButton({ href, label }: { href?: string | null; label: string }) {
  if (!href) return null;
  return (
    <a className="btn sm" href={href} target="_blank" rel="noreferrer">
      <Icon name="link" />
      {label}
    </a>
  );
}

function TripView() {
  const { eventId = '' } = useParams();
  const { store, unavailable } = useOffline();
  const navigate = useNavigate();
  const { can, user } = useSignedIn();
  const [page, setPage] = useState<TripPage | null>(null);
  const [offline, setOffline] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!store && !unavailable) return;
    let current = true;
    loadTrip(store, eventId).then(
      (r) => {
        if (!current) return;
        setError(null);
        setPage(r.page);
        setOffline(r.offline);
      },
      (err: unknown) => {
        if (!current) return;
        setError(
          err instanceof ApiError && err.status === 404
            ? 'This show does not exist, or it was deleted.'
            : errorMessage(err),
        );
      },
    );
    return () => {
      current = false;
    };
  }, [store, unavailable, eventId, version]);

  // A confirmation being read: the page looks again until it is ready.
  const reading = page?.drafts.some((d) => d.status === 'waiting') ?? false;
  useEffect(() => {
    if (!reading) return;
    const timer = setTimeout(() => setVersion((v) => v + 1), 4000);
    return () => clearTimeout(timer);
  }, [reading, page]);

  if (!page) {
    return (
      <>
        <PageHead title="Travel" />
        {error ? (
          <Notice kind="error">{error}</Notice>
        ) : (
          <div className="spinner" aria-label="Loading" />
        )}
      </>
    );
  }

  const e = page.event;
  const name = eventName(e);
  const tv = page.logistics.fields;
  const d = daysTo(e.starts_on, today());
  const mine = page.items.filter((i) => itemFor(i, user.id));
  const gaps = [...(page.onTeam ? tripGaps(page.items, user.id) : []), ...showGaps(page.logistics)];
  const travel = sortItems(mine.filter((i) => !isFileType(i.type)));
  const files = sortItems(mine.filter((i) => isFileType(i.type)));
  const booth = [
    ['Stand', [tv.hall, tv.booth].filter(Boolean).join(', ')],
    ['Badges', tv.badges],
    ['Setup', tv.setup],
    ['Teardown', tv.teardown],
    ['Shipment', tv.shipment],
    ['Booth builder', tv.builder],
    ['Wi-Fi', tv.wifi],
  ].filter(([, v]) => v);
  const useful = [
    ['Show organizer', tv.organizer],
    ['Emergency', tv.emergency],
    ['Team dinner', tv.dinner],
    ['Notes', tv.notes],
  ].filter(([, v]) => v);
  const nameOf = (id: string) =>
    id === user.id
      ? `${user.firstName} ${user.lastName}`
      : (page.team.find((m) => m.id === id)?.name ?? 'Not on the team any more');
  const card = (item: TravelItem) => (
    <ItemCard key={item.id} item={item} eventName={name} nameOf={nameOf} />
  );

  return (
    <>
      <PageHead
        title={name}
        actions={
          <>
            {can('travel.team.view') && (
              <button type="button" className="btn sm" onClick={() => navigate('team')}>
                Team view
              </button>
            )}
            {can('travel.logistics.manage') && (
              <button type="button" className="btn sm" onClick={() => navigate('logistics')}>
                Edit show logistics
              </button>
            )}
            <button type="button" className="btn sm" onClick={() => navigate('/travel')}>
              All trips
            </button>
          </>
        }
      />
      {offline && <Notice>You are offline: this page as this device last saw it.</Notice>}
      <div className="trip-hero">
        <div>
          <span className="lbl">
            {d !== null && d > 0 ? `In ${d} day${d > 1 ? 's' : ''}` : 'Happening now'}
          </span>
          <b>{eventDates(e)}</b>
          <span>{e.location ?? ''}</span>
        </div>
        {tv.booth && (
          <div className="trip-booth">
            <span className="lbl">Booth</span>
            <b>{tv.booth}</b>
            <span>{tv.hall ?? ''}</span>
          </div>
        )}
      </div>
      {/* Round 2: before the show, this device's offline content. */}
      <PrepareOffline event={e} />
      {gaps.length > 0 && (
        <div className="dup" role="status">
          <b>Missing</b>
          <span>{gaps.join(', ')}.</span>
        </div>
      )}
      {page.onTeam && page.drafts.length > 0 && (
        <Section title="To confirm" icon="mail">
          {page.drafts.map((d) => (
            <DraftCard key={d.id} draft={d} onDismissed={() => setVersion((v) => v + 1)} />
          ))}
        </Section>
      )}
      {page.onTeam && (
        <Section
          title="My bookings"
          icon="plane"
          action={
            page.canAdd && !offline ? (
              <button type="button" className="btn sm primary" onClick={() => navigate('add')}>
                + Add
              </button>
            ) : undefined
          }
        >
          {travel.length ? travel.map(card) : <p className="note">Nothing booked yet.</p>}
        </Section>
      )}
      {page.onTeam && files.length > 0 && (
        <Section title="Badges, plans and documents" icon="ticket">
          {files.map(card)}
        </Section>
      )}
      {(tv.venue || tv.address) && (
        <Section title="Venue" icon="pin">
          <div className="tcardx">
            <b>{tv.venue ?? ''}</b>
            <span className="note">{tv.address ?? ''}</span>
            <Row label="Exhibitor entrance">{tv.entrance}</Row>
            <Row label="Parking">{tv.parking}</Row>
            <div className="actions">
              <LinkButton href={tv.address ? mapLink(tv.address) : null} label="Map" />
              <LinkButton href={tv.site} label="Show website" />
            </div>
          </div>
        </Section>
      )}
      {page.logistics.hours.length > 0 && (
        <Section title="Opening hours" icon="clock">
          <div className="tcardx">
            {page.logistics.hours.map((h) => (
              <div key={h.date} className={`trow${h.date === today() ? ' today' : ''}`}>
                <span>
                  {fmtDay(h.date)}
                  {h.label && (
                    <>
                      {' '}
                      <small>{h.label}</small>
                    </>
                  )}
                </span>
                <b>
                  {h.open} to {h.close}
                </b>
              </div>
            ))}
            {!page.logistics.hours_confirmed && (
              <p className="note travel-hours-note">Not confirmed yet.</p>
            )}
          </div>
        </Section>
      )}
      {booth.length > 0 && (
        <Section title="Booth and badges" icon="ticket">
          <div className="tcardx">
            {booth.map(([label, value]) => (
              <Row key={label} label={label!}>
                {value}
              </Row>
            ))}
          </div>
        </Section>
      )}
      {page.documents.length > 0 && (
        <Section title="Documents" icon="pdf">
          <div className="tcardx">
            {page.documents.map((doc) => (
              <div key={doc.id} className="trow">
                <span>
                  <Icon name="pdf" /> {doc.name}
                </span>
                <b>
                  <button
                    type="button"
                    className="linkbtn"
                    aria-label={`Open ${doc.name}`}
                    onClick={() => void openFile(documentUrl(doc.id))}
                  >
                    Open
                  </button>
                </b>
              </div>
            ))}
          </div>
        </Section>
      )}
      {page.team.length > 0 && (
        <Section title="Team on site" icon="contacts">
          <div className="tcardx">
            {page.team.map((m) => (
              <div key={m.id} className="trow">
                <span>
                  {m.name}
                  {m.id === user.id ? ' (you)' : ''}
                  {m.mobile && (
                    <>
                      <br />
                      <a href={`tel:${m.mobile.replace(/[^\d+]/g, '')}`}>{m.mobile}</a>
                    </>
                  )}
                </span>
                <b className="travel-team-right">
                  {m.arrival ? arrival(m.arrival) : 'No flight yet'}
                  {m.gaps.length > 0 && (
                    <>
                      <br />
                      <small className="due-over">{m.gaps.join(', ')}</small>
                    </>
                  )}
                </b>
              </div>
            ))}
          </div>
        </Section>
      )}
      {useful.length > 0 && (
        <Section title="Useful" icon="phone">
          <div className="tcardx">
            {useful.map(([label, value]) => (
              <Row key={label} label={label!}>
                {value}
              </Row>
            ))}
          </div>
        </Section>
      )}
      <Outlet context={{ page, reload: () => setVersion((v) => v + 1) } satisfies TripContext} />
    </>
  );
}

/** "AC 846, lands Mon, Oct 26, 08:10" from the API's "AC 846, lands 2026-10-26T08:10". */
function arrival(text: string) {
  const at = /lands (\S+)$/.exec(text)?.[1];
  return at && at !== '?' ? text.replace(at, fmtDateTime(at)) : text.replace(', lands ?', '');
}

/** "Oct 29, 2026", as the prototype dates a change. */
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export const useTrip = () => useOutletContext<TripContext>();

/** A booking, a badge or a document (prototype: bookingCard). */
function ItemCard({
  item,
  eventName: name,
  nameOf,
}: {
  item: TravelItem;
  eventName: string;
  nameOf(userId: string): string;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const f = item.fields;
  const route = (
    from: string | undefined,
    depart: string | undefined,
    to: string | undefined,
    arrive: string | undefined,
    icon: IconName,
  ) => (
    <div className="fl-route">
      <div>
        <b>{from}</b>
        <span>{fmtDateTime(depart)}</span>
      </div>
      <span className="fl-arrow">
        <Icon name={icon} />
      </span>
      <div className="fl-end">
        <b>{to}</b>
        <span>{fmtDateTime(arrive)}</span>
      </div>
    </div>
  );
  const ics = travelIcs(item, name);
  const label = itemTitle(item);
  return (
    <div className="tcardx" aria-label={`${label}`}>
      {item.type === 'flight' && (
        <>
          <div className="fl-top">
            <b>{label}</b>
            <span className="badge b-off">{f.dir === 'back' ? 'Return' : 'Outbound'}</span>
          </div>
          {route(f.from, f.depart, f.to, f.arrive, 'plane')}
          <Row label="Booking reference">{item.reference}</Row>
          <Row label="Seat">{f.seat}</Row>
        </>
      )}
      {item.type === 'train' && (
        <>
          <div className="fl-top">
            <b>{label}</b>
            <span className="badge b-off">{f.dir === 'back' ? 'Return' : 'Outbound'}</span>
          </div>
          {route(f.from, f.depart, f.to, f.arrive, 'train')}
          <Row label="Ticket">{item.reference}</Row>
          <Row label="Coach and seat">{f.seat}</Row>
        </>
      )}
      {item.type === 'car' && (
        <>
          <div className="fl-top">
            <b>{label}</b>
            <span className="badge b-off">Car rental</span>
          </div>
          <Row label="Pick-up">{f.pickup}</Row>
          <Row label="From">{fmtDateTime(f.pickup_at)}</Row>
          <Row label="Return">{fmtDateTime(f.return_at)}</Row>
          <Row label="Confirmation">{item.reference}</Row>
        </>
      )}
      {item.type === 'hotel' && (
        <>
          <b>{label}</b>
          <span className="note">{f.address ?? ''}</span>
          <Row label="Check-in">
            {f.check_in &&
              `${fmtDay(f.check_in)}${f.check_in_time ? `, from ${f.check_in_time}` : ''}`}
          </Row>
          <Row label="Check-out">
            {f.check_out &&
              `${fmtDay(f.check_out)}${f.check_out_time ? `, by ${f.check_out_time}` : ''}`}
          </Row>
          <Row label="Confirmation">{item.reference}</Row>
          <Row label="To the venue">{f.to_venue}</Row>
        </>
      )}
      {item.type === 'other' && (
        <>
          <b>{label}</b>
          {f.detail && <span className="note">{f.detail}</span>}
          <Row label="When">{fmtDateTime(f.when)}</Row>
          <Row label="Reference">{item.reference}</Row>
        </>
      )}
      {isFileType(item.type) && (
        <>
          <div className="fl-top">
            <b>{label}</b>
            <span className="badge b-off">
              {item.type === 'badge' ? 'Badge' : f.kind === 'doc' ? 'Document' : 'Plan'}
            </span>
          </div>
          {f.detail && <span className="note">{f.detail}</span>}
          <Row label="Code">{item.reference}</Row>
          <Row label="File">{item.file_name}</Row>
        </>
      )}
      <div className="tv-who">
        {item.everyone ? (
          <span className="tag">Everyone on the team</span>
        ) : (
          item.travelers.map((id) => (
            <span key={id} className="tag">
              {nameOf(id)}
            </span>
          ))
        )}
      </div>
      <p className="note travel-note">
        Added by {item.added_by_name || 'a former user'}, {fmtDate(item.updated_at)}
      </p>
      <div className="actions">
        <LinkButton
          href={item.link}
          label={
            item.type === 'hotel' ? 'Reservation' : isFileType(item.type) ? 'Open link' : 'Booking'
          }
        />
        <LinkButton href={f.checkin} label="Check-in" />
        {item.type === 'hotel' && f.address && <LinkButton href={mapLink(f.address)} label="Map" />}
        {item.has_file && (
          <button
            type="button"
            className="btn sm"
            onClick={() => void openFile(itemFileUrl(item.id))}
          >
            <Icon name="pdf" />
            Open file
          </button>
        )}
        {item.reference && (
          <button
            type="button"
            className="btn sm"
            onClick={() =>
              void navigator.clipboard
                ?.writeText(item.reference ?? '')
                .then(() => toast('Reference copied'))
            }
          >
            Copy reference
          </button>
        )}
        {ics && (
          <button
            type="button"
            className="btn sm"
            onClick={() =>
              saveText(
                `${label.replace(/[^a-z0-9]+/gi, '_') || 'booking'}.ics`,
                ics,
                'text/calendar',
              )
            }
          >
            <Icon name="calendar" />
            Add to calendar
          </button>
        )}
        <button
          type="button"
          className="btn sm"
          aria-label={`Edit ${label}`}
          onClick={() => navigate(`items/${item.id}`)}
        >
          <Icon name="edit" />
          Edit
        </button>
      </div>
    </div>
  );
}

/** A confirmation forwarded or uploaded, read (or being read) by Claude. */
/** "Flight, Air Canada AC 846", or every booking read (round 2): "Flight, NW 318; Flight, NW 317". */
function readSummary(result: ConfirmationReading | null): string {
  const items = result ? readingItems(result) : [];
  if (items.length === 0) return 'to complete';
  return items
    .map((i) => `${TRAVEL_TYPE_LABELS[i.type]}, ${itemTitle(i) || 'to complete'}`)
    .join('; ');
}

function DraftCard({ draft, onDismissed }: { draft: TravelDraft; onDismissed(): void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const result = draft.result && 'passengers' in draft.result ? draft.result : null;
  const title = draft.subject || draft.file_name || 'Confirmation';
  async function dismiss() {
    try {
      await del(`/travel/drafts/${draft.id}`);
      toast('Dismissed');
      onDismissed();
    } catch (err) {
      toast(errorMessage(err));
    }
  }
  return (
    <div className="tcardx">
      <div className="fl-top">
        <b>{title}</b>
        <span className="badge b-off">{draft.source === 'email' ? 'Forwarded' : 'Uploaded'}</span>
      </div>
      <span className="note">
        {draft.status === 'waiting'
          ? 'Reading…'
          : draft.status === 'failed'
            ? travelReadFailureText(draft.failure)
            : `Read by Claude: ${readSummary(result)}. Check it before it is saved.`}
      </span>
      <div className="actions">
        {draft.status === 'ready' && (
          <button
            type="button"
            className="btn sm primary"
            aria-label={`Check ${title}`}
            onClick={() => navigate(`drafts/${draft.id}`)}
          >
            Check
          </button>
        )}
        {draft.status === 'failed' && (
          <button
            type="button"
            className="btn sm"
            onClick={() => navigate(`add/${draft.type ?? 'flight'}`)}
          >
            Type it
          </button>
        )}
        {draft.status !== 'waiting' && (
          <button
            type="button"
            className="btn sm"
            aria-label={`Dismiss ${title}`}
            onClick={() => void dismiss()}
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}
